import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  LayoutDashboard,
  ClipboardList,
  CheckSquare,
  BarChart3,
  User,
  Users,
  UserPlus,
  LogOut,
  RefreshCw,
  LucideIcon,
  PanelLeftClose,
  PanelLeft,
  Menu,
  ChevronRight,
  ChevronLeft,
  Settings,
  CreditCard,
  Shield
} from 'lucide-react';
import { cn } from '../lib/utils';
import { getAvatarUrl } from '../lib/avatar';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Sheet, SheetContent, SheetTrigger } from './ui/sheet';
import UserTierBadge from './UserTierBadge';

interface NavItem {
  path: string;
  label: string;
  icon: LucideIcon;
  exact?: boolean;
}

interface SidebarProps {
  defaultCollapsed?: boolean;
}

const Sidebar: React.FC<SidebarProps> = ({ defaultCollapsed = false }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(defaultCollapsed);

  React.useEffect(() => {
    setIsCollapsed(defaultCollapsed);
  }, [defaultCollapsed]);

  const navItems: NavItem[] = [
    { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, exact: true },
    { path: '/retrospectives', label: 'Retrospectives', icon: ClipboardList },
    { path: '/action-items', label: 'Action Items', icon: CheckSquare },
    { path: '/analytics', label: 'Analytics', icon: BarChart3 },
  ];

  const isActive = (path: string, exact?: boolean) => {
    if (exact) {
      return location.pathname === path;
    }
    return location.pathname.startsWith(path);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const MobileMenu = () => (
    <div className="flex flex-col h-full">
      <div className="flex items-center gap-2 p-6 border-b">
        <RefreshCw className="w-6 h-6 text-primary-600" />
        <span className="font-bold text-xl text-gray-900">Retrospace</span>
      </div>
      <nav className="flex-1 px-4 py-6 space-y-1 overflow-y-auto">
        {navItems.map((item) => (
          <Link
            key={item.path}
            to={item.path}
            onClick={() => setIsMobileOpen(false)}
            className={cn(
              "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors",
              isActive(item.path, item.exact)
                ? "bg-primary-50 text-primary-700"
                : "text-gray-700 hover:bg-gray-100 hover:text-gray-900"
            )}
          >
            <item.icon className={cn("w-5 h-5", isActive(item.path, item.exact) ? "text-primary-600" : "text-gray-500")} />
            {item.label}
          </Link>
        ))}

        <div className="px-3 py-2 mt-4 text-xs font-semibold text-gray-400 uppercase tracking-wider">
          Workspace
        </div>
        <Link
          to="/teams"
          onClick={() => setIsMobileOpen(false)}
          className={cn(
            "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors",
            isActive('/teams')
              ? "bg-primary-50 text-primary-700"
              : "text-gray-700 hover:bg-gray-100 hover:text-gray-900"
          )}
        >
          <Users className={cn("w-5 h-5", isActive('/teams') ? "text-primary-600" : "text-gray-500")} />
          Team Management
        </Link>

        {user?.role === 'admin' && (
          <>
            <div className="px-3 py-2 mt-4 text-xs font-semibold text-gray-400 uppercase tracking-wider">
              Admin
            </div>
            <Link
              to="/admin"
              onClick={() => setIsMobileOpen(false)}
              className={cn(
                "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors",
                isActive('/admin')
                  ? "bg-red-50 text-red-700"
                  : "text-gray-700 hover:bg-red-50 hover:text-red-700"
              )}
            >
              <Shield className={cn("w-5 h-5", isActive('/admin') ? "text-red-600" : "text-gray-500")} />
              Admin Panel
            </Link>
          </>
        )}
      </nav>
      <div className="p-4 border-t">
        <div className="flex items-center gap-3 mb-4 px-2">
          <Avatar className="h-8 w-8">
            <AvatarImage src={getAvatarUrl(user?.name, user?.avatar)} alt={user?.name} />
            <AvatarFallback className="bg-primary-50 text-primary-700 text-xs font-semibold border border-primary-100">
              {user?.name?.charAt(0).toUpperCase() || 'U'}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 overflow-hidden">
            <p className="text-sm font-medium text-gray-900 truncate">{user?.name}</p>
            <p className="text-xs text-gray-500 truncate">{user?.email}</p>
          </div>
        </div>
        <div className="space-y-1">
           <Link
            to="/profile"
            onClick={() => setIsMobileOpen(false)}
            className="flex items-center gap-3 px-3 py-2 text-sm font-medium text-gray-700 rounded-lg hover:bg-gray-100"
          >
            <User className="w-4 h-4" />
            My Profile
          </Link>
          <button
            onClick={handleLogout}
            className="flex w-full items-center gap-3 px-3 py-2 text-sm font-medium text-gray-700 rounded-lg hover:bg-red-50 hover:text-red-700 transition-colors"
          >
            <LogOut className="w-4 h-4" />
            Sign Out
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <>
      {/* Mobile Top Bar */}
      <div className="md:hidden sticky top-0 z-40 w-full bg-white/80 backdrop-blur-md border-b border-gray-200 px-4 h-16 flex items-center justify-between">
        <div className="flex items-center gap-2">
           <Sheet open={isMobileOpen} onOpenChange={setIsMobileOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="-ml-2">
                <Menu className="w-6 h-6 text-gray-700" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="p-0 w-[280px]">
              <MobileMenu />
            </SheetContent>
          </Sheet>
          <span className="font-bold text-lg text-gray-900">Retrospace</span>
        </div>
        <Avatar className="h-8 w-8">
           <AvatarImage src={getAvatarUrl(user?.name, user?.avatar)} alt={user?.name} />
           <AvatarFallback className="bg-primary-600 text-white text-xs">
              {user?.name?.charAt(0).toUpperCase() || 'U'}
            </AvatarFallback>
        </Avatar>
      </div>

      {/* Desktop Sidebar */}
      <aside className={cn(
        "hidden md:flex flex-col h-screen sticky top-0 border-r border-gray-200 bg-white shadow-sm z-30 transition-all duration-300",
        isCollapsed ? "w-[80px]" : "w-[260px]"
      )}>
        <div className="absolute -right-3 top-9 z-50">
          <Button
            variant="outline"
            size="icon"
            className="h-6 w-6 rounded-full shadow-md bg-white border border-gray-200 hover:bg-gray-50 text-gray-500"
            onClick={() => setIsCollapsed(!isCollapsed)}
            aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            {isCollapsed ? <ChevronRight className="w-3 h-3" /> : <ChevronLeft className="w-3 h-3" />}
          </Button>
        </div>

        <div className={cn("p-6 flex items-center", isCollapsed ? "justify-center px-4" : "")}>
          <Link to="/dashboard" className="flex items-center gap-2.5 overflow-hidden">
            <div className="bg-primary-600 p-1.5 rounded-lg shadow-sm flex-shrink-0">
                <RefreshCw className="w-5 h-5 text-white" />
            </div>
            <span className={cn(
                "font-bold text-lg tracking-tight text-gray-900 transition-opacity duration-300 whitespace-nowrap",
                isCollapsed ? "opacity-0 w-0 hidden" : "opacity-100"
            )}>Retrospace</span>
          </Link>
        </div>

        <div className="flex-1 flex flex-col gap-1 px-3 py-4">
            <div className={cn("px-3 mb-2 transition-opacity duration-300", isCollapsed ? "opacity-0 hidden" : "opacity-100")}>
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Main Menu</p>
            </div>
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              title={isCollapsed ? item.label : undefined}
              className={cn(
                "group flex items-center px-3 py-2 rounded-md text-sm font-medium transition-all duration-200",
                isActive(item.path, item.exact)
                  ? "bg-primary-50 text-primary-700 shadow-sm ring-1 ring-primary-200/50"
                  : "text-gray-600 hover:bg-gray-50 hover:text-gray-900",
                isCollapsed ? "justify-center" : "justify-between"
              )}
            >
              <div className="flex items-center gap-3">
                <item.icon className={cn(
                    "w-4 h-4 transition-colors flex-shrink-0",
                    isActive(item.path, item.exact) ? "text-primary-600" : "text-gray-400 group-hover:text-gray-600"
                )} />
                {!isCollapsed && <span className="whitespace-nowrap">{item.label}</span>}
              </div>
              {!isCollapsed && isActive(item.path, item.exact) && (
                  <ChevronRight className="w-4 h-4 text-primary-400 opacity-50" />
              )}
            </Link>
          ))}
          
           <div className={cn("mt-8 px-3 mb-2 transition-opacity duration-300", isCollapsed ? "opacity-0 hidden" : "opacity-100")}>
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Workspace</p>
            </div>
            <Link
              to="/teams"
              title={isCollapsed ? "Team Management" : undefined}
              className={cn(
                "group flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-all duration-200",
                isActive('/teams') 
                  ? "bg-primary-50 text-primary-700 shadow-sm ring-1 ring-primary-200/50" 
                  : "text-gray-600 hover:bg-gray-50 hover:text-gray-900",
                isCollapsed ? "justify-center" : ""
              )}
            >
              <Users className={cn("w-4 h-4 flex-shrink-0", isActive('/teams') ? "text-primary-600" : "text-gray-400 group-hover:text-gray-600")} />
              {!isCollapsed && <span className="whitespace-nowrap">Team Management</span>}
            </Link>

            {user?.role === 'admin' && (
              <>
                <div className={cn("mt-8 px-3 mb-2 transition-opacity duration-300", isCollapsed ? "opacity-0 hidden" : "opacity-100")}>
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Admin</p>
                </div>
                <Link
                  to="/admin"
                  title={isCollapsed ? "Admin Panel" : undefined}
                  className={cn(
                    "group flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-all duration-200",
                    isActive('/admin') 
                      ? "bg-primary-50 text-red-700 shadow-sm ring-1 ring-red-200/50" 
                      : "text-gray-600 hover:bg-red-50 hover:text-red-700",
                    isCollapsed ? "justify-center" : ""
                  )}
                >
                  <Shield className={cn("w-4 h-4 flex-shrink-0", isActive('/admin') ? "text-red-600" : "text-gray-400 group-hover:text-red-600")} />
                  {!isCollapsed && <span className="whitespace-nowrap">Admin Panel</span>}
                </Link>
              </>
            )}

        </div>

        {/* User Footer */}
         <div className="p-4 border-t border-gray-100 bg-gray-50/50">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className={cn(
                  "flex items-center gap-3 w-full p-2 rounded-lg hover:bg-white hover:shadow-sm hover:ring-1 hover:ring-gray-200 transition-all",
                  isCollapsed ? "justify-center" : ""
              )}>
                <Avatar className="h-9 w-9 border border-white shadow-sm flex-shrink-0">
                  <AvatarImage src={getAvatarUrl(user?.name, user?.avatar)} alt={user?.name} />
                  <AvatarFallback className="bg-primary-600 text-white text-xs font-medium">
                    {user?.name?.charAt(0).toUpperCase() || 'U'}
                  </AvatarFallback>
                </Avatar>
                {!isCollapsed && (
                    <>
                        <div className="flex-1 text-left overflow-hidden">
                          <p className="text-sm font-semibold text-gray-900 truncate">{user?.name}</p>
                          <div className="flex items-center gap-2">
                             <UserTierBadge tier={user?.tier} size="sm" variant="outline" className="h-5 border-opacity-50" />
                          </div>
                        </div>
                        <Settings className="w-4 h-4 text-gray-400" />
                    </>
                )}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" side="right" sideOffset={10} className="w-56">
                <DropdownMenuItem asChild>
                    <Link to="/profile" className="cursor-pointer">
                        <User className="w-4 h-4 mr-2" />
                        Profile Settings
                    </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                    <Link to="/settings/billing" className="cursor-pointer">
                        <CreditCard className="w-4 h-4 mr-2" />
                        Billing & Plans
                    </Link>
                </DropdownMenuItem>
               <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} className="text-red-600 focus:text-red-600 focus:bg-red-50 cursor-pointer">
                <LogOut className="w-4 h-4 mr-2" />
                Log out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
